import { ArrowRight, CheckCircle2, Phone, Truck, Package, Shield, FileCheck, Clock, Home, Building2, Sparkles, Boxes, ChevronDown } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Contact from '../components/Contact';
import FAQ from '../components/FAQ';

export default function Leistungen() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('kontakt');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollDown = () => {
    window.scrollBy({ top: window.innerHeight, behavior: 'smooth' });
  };

  const mainServices = [
    {
      icon: Truck,
      title: 'Komplettumzug',
      description: 'Rundum-Sorglos-Paket von der Planung bis zur Schlüsselübergabe. Wir kümmern uns um alles: Verpackung, Transport, Zollabwicklung und Aufbau.',
      features: [
        'Komplette Verpackung Ihres Hausrats',
        'Professionelle Möbelmontage',
        'Transport mit Versicherung',
        'Zollabwicklung inklusive',
      ],
      link: '/komplettumzug',
    },
    {
      icon: Package,
      title: 'Teilumzug',
      description: 'Flexible Lösung für kleinere Umzüge oder Transporte einzelner Möbel und Gegenstände von der Schweiz nach Deutschland.',
      features: [
        'Transport einzelner Möbelstücke',
        'Beiladung möglich',
        'Kosteneffiziente Lösung',
        'Flexible Terminplanung',
      ],
      link: '/teilumzug',
    },
    {
      icon: FileCheck,
      title: 'Zollabwicklung',
      description: 'Professionelle Abwicklung aller Zollformalitäten für Ihren Umzug von der Schweiz nach Deutschland – schnell und unkompliziert.',
      features: [
        'Erstellung aller Dokumente',
        'Übersiedlungsgut-Anmeldung',
        'Begleitung am Grenzübergang',
        'Beratung zu Zollbestimmungen',
      ],
      link: '/zollabwicklung',
    },
    {
      icon: Clock,
      title: 'Express-Umzüge',
      description: 'Schnelle Umzugslösung innerhalb von 24-48 Stunden für dringende Umzüge von der Schweiz nach Deutschland.',
      features: [
        'Umzug innerhalb 24-48 Stunden',
        'Prioritätsbehandlung',
        'Direkttransport ohne Umladung',
        'Sofortige Terminvergabe',
      ],
      link: '/express-umzuege',
    },
    {
      icon: Building2,
      title: 'Firmenumzüge',
      description: 'Spezialisierte Büro- und Firmenumzüge mit minimaler Ausfallzeit für Ihr Unternehmen beim Wechsel nach Deutschland.',
      features: [
        'Umzug außerhalb Geschäftszeiten',
        'IT-Equipment Transport',
        'Minimale Betriebsunterbrechung',
        'Projektmanagement inklusive',
      ],
      link: '/firmenumzuege',
    },
    {
      icon: Boxes,
      title: 'Verpackungsmaterial',
      description: 'Hochwertiges Verpackungsmaterial für einen sicheren Transport Ihres Umzugsguts über die Grenze.',
      features: [
        'Stabile Umzugskartons',
        'Spezialmaterial für Glas & Porzellan',
        'Möbeldecken & Luftpolsterfolie',
        'Lieferung direkt zu Ihnen',
      ],
      link: '/verpackungsmaterial',
    },
  ];

  const additionalServices = [
    {
      icon: Home,
      title: 'Zwischenlagerung',
      description: 'Sichere Lagerung Ihres Hausrats in unseren Lagerhallen, falls Ihre neue Wohnung noch nicht bezugsbereit ist.',
      link: '/zwischenlagerung',
    },
    {
      icon: Sparkles,
      title: 'Endreinigung',
      description: 'Professionelle Endreinigung Ihrer alten Wohnung in der Schweiz für eine problemlose Wohnungsabgabe.',
      link: '/endreinigung',
    },
  ];

  const benefits = [
    'Über 15 Jahre Erfahrung mit Grenzumzügen',
    'Fixpreis-Garantie ohne versteckte Kosten',
    'Vollständig versichert während des Transports',
    'Express-Service innerhalb 24-48 Stunden',
    'Professionelles und erfahrenes Team',
    'Kostenlose Besichtigung vor Ort',
  ];

  return (
    <div className="min-h-screen">
      <section className="relative pt-24 md:pt-32 pb-24 min-h-[700px] bg-gradient-to-br from-slate-50 via-white to-slate-50 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-slate-500/5 animate-gradient"></div>
          <img
            src="/umzugsunternehmen-schweiz-deutschland.jpeg"
            alt="Umzug Leistungen Schweiz Deutschland"
            className="w-full h-full object-cover opacity-10"
          />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-green-50 text-green-700 px-4 py-2 rounded-full mb-6 border border-green-200 animate-fade-in hover:scale-105 transition-transform duration-300">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-sm font-semibold">Umfassende Umzugsleistungen für jeden Bedarf</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight animate-fade-in-delay">
              Unsere Leistungen
              <span className="block text-slate-700 text-4xl md:text-5xl mt-2">
                Professionelle Umzüge Schweiz → Deutschland
              </span>
            </h1>

            <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto leading-relaxed animate-fade-in-delay-2">
              Von der Planung bis zur Schlüsselübergabe: Entdecken Sie unser komplettes Leistungsspektrum für Ihren stressfreien Umzug von der Schweiz nach Deutschland.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 mb-12 animate-fade-in-delay-2">
              <button
                onClick={scrollToContact}
                className="group bg-slate-800 hover:bg-slate-900 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 flex items-center space-x-2 text-lg"
              >
                <span>Jetzt kostenloses Angebot erhalten</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="tel:0800700100"
                className="bg-white hover:bg-slate-50 text-slate-800 px-8 py-4 rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-xl hover:scale-105 border-2 border-slate-800"
              >
                0800 700 100 (Gratis)
              </a>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto mb-16">
              <div className="bg-white rounded-xl p-6 shadow-md border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 animate-fade-in-delay-2">
                <div className="text-3xl font-bold text-slate-800 mb-1">8+</div>
                <div className="text-slate-600 text-sm">Umzugsleistungen</div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 animate-fade-in-delay-2">
                <div className="text-3xl font-bold text-slate-800 mb-1">15+</div>
                <div className="text-slate-600 text-sm">Jahre Erfahrung</div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 animate-fade-in-delay-2">
                <div className="text-3xl font-bold text-slate-800 mb-1">100%</div>
                <div className="text-slate-600 text-sm">Kundenzufriedenheit</div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
          <button
            onClick={scrollDown}
            className="animate-bounce bg-white hover:bg-slate-50 text-slate-800 p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-slate-200"
            aria-label="Scroll down"
          >
            <ChevronDown className="w-6 h-6" />
          </button>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8 text-center">
              Unsere Hauptleistungen für Ihren Umzug
            </h2>

            <div className="prose prose-lg max-w-none text-slate-700 mb-12">
              <p className="text-xl leading-relaxed mb-6 text-center">
                Als Spezialisten für <strong>Umzüge von der Schweiz nach Deutschland</strong> bieten wir Ihnen ein umfassendes Leistungspaket. Von der ersten Planung über den Transport bis hin zur vollständigen Einrichtung am Zielort – wir sind Ihr verlässlicher Partner für jeden Schritt Ihres <strong>Grenzumzugs</strong>.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto mb-12">
            {mainServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className="bg-slate-50 rounded-2xl p-8 border-2 border-slate-100 hover:border-slate-800 transition-all duration-300 hover:shadow-xl hover:-translate-y-2 group cursor-pointer"
                  onClick={() => navigate(service.link)}
                >
                  <div className="bg-slate-800 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center text-slate-800 font-semibold group-hover:translate-x-2 transition-transform">
                    <span>Mehr erfahren</span>
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8 text-center">
              Zusätzliche Services
            </h2>
            <p className="text-xl text-slate-600 text-center max-w-2xl mx-auto">
              Ergänzende Dienstleistungen für einen reibungslosen Umzug
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {additionalServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-8 border-2 border-slate-100 hover:border-slate-800 transition-all duration-300 hover:shadow-xl hover:-translate-y-2 group cursor-pointer"
                  onClick={() => navigate(service.link)}
                >
                  <div className="bg-slate-800 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <div className="flex items-center text-slate-800 font-semibold group-hover:translate-x-2 transition-transform">
                    <span>Mehr erfahren</span>
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-12 text-center">
              Warum unsere Leistungen wählen?
            </h2>

            <div className="bg-slate-50 rounded-2xl p-8 border-2 border-slate-100">
              <div className="grid md:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                    <span className="text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-12 bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-10 text-center text-white">
              <Shield className="w-16 h-16 mx-auto mb-6 text-green-400" />
              <h3 className="text-3xl font-bold mb-4">
                Ihr Umzug in sicheren Händen
              </h3>
              <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed">
                Mit unserer langjährigen Erfahrung, professionellem Equipment und qualifizierten Mitarbeitern garantieren wir Ihnen einen stressfreien Umzug von der Schweiz nach Deutschland.
              </p>
              <button
                onClick={scrollToContact}
                className="bg-white text-slate-900 px-8 py-4 rounded-lg font-semibold hover:bg-slate-100 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 inline-flex items-center space-x-2"
              >
                <span>Jetzt unverbindlich beraten lassen</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Bereit für Ihren Umzug?
            </h2>
            <p className="text-xl text-slate-600 mb-8">
              Kontaktieren Sie uns für ein kostenloses und unverbindliches Angebot
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <button
                onClick={scrollToContact}
                className="group bg-slate-800 hover:bg-slate-900 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 flex items-center space-x-2"
              >
                <span>Kostenloses Angebot anfordern</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="tel:0800700100"
                className="flex items-center space-x-2 text-slate-800 hover:text-slate-900 font-semibold transition"
              >
                <Phone className="w-5 h-5" />
                <span>0800 700 100 (Gratis)</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <FAQ />
      <Contact />
    </div>
  );
}
